import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import isEqual from 'lodash/isEqual'
import last from 'lodash/last'
import { ModalWrapper } from '@microservices/wiskey-react-components'
import { Box } from '@mui/material'

import { ModalFormContainer } from '@components/ModalFormContainer'
import {
  AlignmentType,
  ExternalTextSettings,
  GetTextParamsHandle,
  INITIAL_TEXT_STATE,
  TextSettingsStateType,
} from '@components/TextSettings'

import { useYupValidationResolver } from '@hooks'
import { getNewLastIndexByData } from '@helpers'
import { AutocompleteOption, ObjectFieldDTO } from '@types'

import { PageContext, SectionType, SegmentType } from '@gantt/components/GanttCreateOrEdit'

import { sectionFormSchema } from '@validations'

import { getGanttSegmentDefaultValue } from './helpers/getGanttSegmentDefaultValue'
import { useAddGanttSegmentDialog } from './hooks/useAddGanttSegmentDialog'
import { SegmentItem } from './SegmentItem'

type SegmentsArrayProps = {
  type?: SegmentType
  rawObjectFields?: ObjectFieldDTO[]
}

export const AddSegmentDialog = ({
  type = SegmentType.MULTI,
  rawObjectFields,
}: SegmentsArrayProps) => {
  const { showDialog, handleCloseModal, modalType, currentSectionsList, currentRow } =
    useContext(PageContext)

  const resolver = useYupValidationResolver(sectionFormSchema)

  const defaultValues = getGanttSegmentDefaultValue(
    getNewLastIndexByData({
      items: currentSectionsList,
      getterNumber: value => last(value?.link?.systemName?.split('_')),
    }) ?? 0
  )

  const methods = useForm<SectionType>({
    defaultValues,
    resolver,
  })

  const {
    handleSubmit,
    formState: { isDirty: isFormDirty, errors },
    reset,
    setError,
    clearErrors,
  } = methods

  const { t } = useTranslation()

  const textSettingsRef = useRef<GetTextParamsHandle>(null)
  const [isTextSettingsDirty, setIsTextSettingsDirty] = useState(false)

  const isDirty = isFormDirty || isTextSettingsDirty

  const initialTextState = useMemo<TextSettingsStateType>(() => {
    const ts = currentRow?.textSettings

    if (ts?.fontFamily) {
      return {
        fontFamily: { id: ts.fontFamily, label: ts.fontFamily } as AutocompleteOption,
        fontSize: ts.fontSize ?? INITIAL_TEXT_STATE.fontSize,
        formats: [
          ...(ts.isBold ? ['bold' as const] : []),
          ...(ts.isItalic ? ['italic' as const] : []),
        ],
        alignment: (ts.alignment as AlignmentType) ?? INITIAL_TEXT_STATE.alignment,
        fontColor: ts.fontColor ?? INITIAL_TEXT_STATE.fontColor,
      }
    }

    return INITIAL_TEXT_STATE
  }, [currentRow])

  const handleTextSettingsDirty = (value: ExternalTextSettings) => {
    if (value && initialTextState) {
      setIsTextSettingsDirty(!isEqual(value, initialTextState))
    }
  }

  const handleDuplicateError = (error: boolean) => {
    if (error) {
      setError('link.systemName', { type: 'custom', message: t('error.duplicatedSystemName') })

      return
    }

    clearErrors('link.systemName')
  }

  const { handleSave } = useAddGanttSegmentDialog({
    reset,
    handleSubmit,
    textSettingsRef,
    onDuplicateError: handleDuplicateError,
    modalType,
  })

  const handleOnCloseModal = useCallback(() => {
    handleCloseModal(isDirty)
  }, [isDirty])

  const getModalTitle = useMemo(() => {
    return modalType === 'edit'
      ? t('ganttCreate.timelineForm.segmentDialogEdit')
      : t('ganttCreate.timelineForm.segmentDialogCreate')
  }, [modalType])

  return (
    <FormProvider {...methods}>
      {showDialog && (
        <ModalWrapper
          btnText={t('ganttCreate.common.apply')}
          disabledSave={!isDirty}
          isShow={showDialog}
          title={getModalTitle}
          onClose={handleOnCloseModal}
          onSave={handleSave}
        >
          <ModalFormContainer maxHeight={'calc(100vh/1.5)'} width={'calc(100vw/1.5)'}>
            <Box mb={1}>
              <SegmentItem
                errors={errors}
                initialTextState={initialTextState}
                objectFieldsForValidation={rawObjectFields}
                textSettingsRef={textSettingsRef}
                type={type}
                onTextSettingsDirty={handleTextSettingsDirty}
              />
            </Box>
          </ModalFormContainer>
        </ModalWrapper>
      )}
    </FormProvider>
  )
}
